import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { fmtRM } from "@/lib/format";
import { CalendarClock, CalendarPlus, Clock, Flag, Skull, ChevronRight, MessageCircle, ArrowUp, ArrowDown, ChevronsUpDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

const STATUS_CLS = {
  Due: "bg-slate-100 text-slate-600 border-slate-200",
  Contacted: "bg-blue-50 text-blue-700 border-blue-200",
  "Promised to Pay": "bg-violet-50 text-violet-700 border-violet-200",
  "No Answer": "bg-amber-50 text-amber-700 border-amber-200",
  "Cancelled Call": "bg-amber-50 text-amber-700 border-amber-200",
  "Invalid Number": "bg-rose-50 text-rose-700 border-rose-200",
  Refused: "bg-rose-50 text-rose-700 border-rose-200",
  Partial: "bg-orange-50 text-orange-700 border-orange-200",
  Collected: "bg-green-50 text-green-700 border-green-200",
};

// Sortable header cell
function Th({ label, sortKey, sort, onSort, align = "left", className = "" }) {
  const alignCls = align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left";
  const active = sort.key === sortKey;
  const sortable = !!sortKey;
  return (
    <th className={`px-3 py-3 font-medium ${alignCls} ${className}`}>
      {sortable ? (
        <button
          type="button"
          data-testid={`sort-${sortKey}`}
          onClick={() => onSort(sortKey)}
          className={`inline-flex items-center gap-1 hover:text-slate-900 transition-colors ${active ? "text-slate-900" : ""} ${align === "right" ? "flex-row-reverse" : ""}`}
        >
          {label}
          {active ? (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ChevronsUpDown className="h-3 w-3 opacity-40" />}
        </button>
      ) : (
        label
      )}
    </th>
  );
}

function DaysCell({ days }) {
  if (days === null || days === undefined) return <td className="px-3 py-3 text-right font-mono-fig text-slate-300">—</td>;
  const cls = days > 60 ? "text-red-600 font-semibold" : days > 31 ? "text-amber-600" : "text-slate-600";
  return (
    <td className="px-3 py-3 text-right font-mono-fig">
      <span className={cls} title="Days since the customer's last payment">{days}d</span>
    </td>
  );
}

function OverdueCell({ days }) {
  if (days === null || days === undefined) return <td className="px-3 py-3 text-right font-mono-fig text-slate-300">—</td>;
  const cls = days > 60 ? "text-red-700 font-bold" : days > 14 ? "text-red-600 font-semibold" : "text-amber-600";
  return (
    <td className="px-3 py-3 text-right font-mono-fig">
      <span className={cls} title="Days the payment is past its due date">{days}d</span>
    </td>
  );
}

function FollowStatusCell({ r, tab }) {
  const { t } = useLang();
  return (
    <td className="px-3 py-3">
      {r.followStatus === "Promised to Pay" ? (
        <Link
          to={`/collection/promise?code=${encodeURIComponent(r.code)}&tab=${tab}`}
          data-testid={`promise-link-${r.code}`}
          className={`inline-block text-xs font-semibold rounded-full px-2.5 py-1 border hover:underline ${STATUS_CLS["Promised to Pay"]}`}
          title="View promise details"
        >
          {t("coll.promised")}{r.promiseDate ? ` · ${r.promiseDate}` : ""}
        </Link>
      ) : (
        <span className={`inline-block text-xs font-semibold rounded-full px-2.5 py-1 border ${STATUS_CLS[r.followStatus] || STATUS_CLS.Due}`}>{r.followStatus || t("coll.due_status")}</span>
      )}
    </td>
  );
}

function WhatsAppCell({ r, onWhatsApp }) {
  return (
    <td className="px-3 py-3 text-center">
      {r.phone ? (
        <button
          type="button"
          data-testid={`whatsapp-${r.code}`}
          onClick={() => onWhatsApp(r.code)}
          title="Open WhatsApp with a pre-filled reminder"
          className="inline-flex items-center gap-1 bg-emerald-600 text-white hover:bg-emerald-700 rounded-md px-2.5 py-1 text-xs font-medium"
        >
          <MessageCircle className="h-3 w-3" /> WhatsApp
        </button>
      ) : (
        <span className="text-slate-300 text-xs">—</span>
      )}
    </td>
  );
}

function ActionCell({ r, tab }) {
  const { t } = useLang();
  return (
    <td className="px-5 py-3 text-right">
      <Link
        to={`/collection/action?code=${encodeURIComponent(r.code)}&tab=${tab}`}
        data-testid={`take-action-${r.code}`}
        className="inline-flex items-center gap-1 bg-slate-900 text-white hover:bg-slate-800 rounded-md px-2.5 py-1 text-xs font-medium"
      >
        {t("coll.take_action")} <ChevronRight className="h-3 w-3" />
      </Link>
    </td>
  );
}

function DueRow({ r, tab, onWhatsApp, showOverdue }) {
  const { t } = useLang();
  return (
    <tr data-testid={`due-row-${r.code}`} className="border-b border-slate-50 hover:bg-slate-50">
      <td className="px-5 py-3"><Link to={`/loans/${r.code}`} className="font-mono-fig font-medium text-slate-900 hover:underline">{r.code}</Link></td>
      <td className="px-3 py-3"><Link to={`/customer?q=${encodeURIComponent(r.customerName)}`} className="text-slate-700 hover:text-slate-900 hover:underline">{r.customerName}</Link></td>
      <td className="px-3 py-3 text-center font-mono-fig text-slate-600">
        <span className="inline-flex items-center gap-1.5">
          {r.loanType === "business" && r.nextDueDate ? <span title={`Every ${r.cycleDays} days`} className="text-xs">{r.nextDueDate}</span> : (r.manualOverride && r.nextDueDate ? <span className="text-xs">{r.nextDueDate}</span> : r.dueDay)}
          {r.manualOverride && <span data-testid={`manual-badge-${r.code}`} title="Next due date was set manually" className="text-[10px] font-bold uppercase tracking-wide bg-violet-100 text-violet-700 rounded px-1.5 py-0.5">{t("coll.manual")}</span>}
        </span>
      </td>
      <td className="px-3 py-3 text-right font-mono-fig text-slate-800">{fmtRM(r.outstanding)}</td>
      <td className="px-3 py-3 text-right font-mono-fig text-red-600">{fmtRM(r.pendingInterest)}</td>
      {showOverdue && <OverdueCell days={r.daysOverdue} />}
      <DaysCell days={r.daysSinceLastPayment} />
      <FollowStatusCell r={r} tab={tab} />
      <WhatsAppCell r={r} onWhatsApp={onWhatsApp} />
      <ActionCell r={r} tab={tab} />
    </tr>
  );
}

// Generic client-side sort
function useSorted(rows, sort) {
  return useMemo(() => {
    if (!sort.key) return rows;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      const aNull = av === null || av === undefined;
      const bNull = bv === null || bv === undefined;
      if (aNull && bNull) return 0;
      if (aNull) return 1; // nulls always last
      if (bNull) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [rows, sort]);
}

export default function CollectionPriority() {
  const { t } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") || "dueToday";
  const setTab = (tk) => setSearchParams({ tab: tk }, { replace: true });
  const [due, setDue] = useState({ dueToday: [], dueTomorrow: [], overdue: [], upcoming: [], nextMonth: [], date: "" });
  const [risk, setRisk] = useState([]);
  const [sort, setSort] = useState({ key: null, dir: "asc" });

  const onSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const load = useCallback(() => {
    api.get("/collections/due").then((r) => setDue(r.data));
    api.get("/reports/collection-priority").then((r) => setRisk(r.data));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openWhatsApp = async (code) => {
    const toneFor = { dueToday: "due_today", dueTomorrow: "due_tomorrow", overdue: "overdue", redFlags: "overdue", whiteBoard: "overdue" };
    const win = window.open("", "_blank");
    try {
      const { data } = await api.get(`/reminders/whatsapp-link/${code}`, { params: { tone: toneFor[tab] || undefined } });
      if (win) win.location.href = data.url;
      else window.location.href = data.url;
    } catch (err) {
      if (win) win.close();
      toast.error(err.response?.data?.detail || t("coll.no_phone"));
    }
  };

  const redFlags = risk.filter((r) => r.category !== "White Board");
  const whiteBoard = risk.filter((r) => r.category === "White Board");

  const TABS = [
    { key: "dueToday", label: t("coll.tab.dueToday"), count: due.dueToday.length, icon: CalendarClock },
    { key: "dueTomorrow", label: t("coll.tab.dueTomorrow"), count: due.dueTomorrow.length, icon: CalendarClock },
    { key: "overdue", label: t("coll.tab.overdue"), count: due.overdue.length, icon: Clock },
    { key: "upcoming", label: t("coll.tab.upcoming"), count: due.upcoming.length, icon: CalendarClock },
    { key: "nextMonth", label: t("coll.tab.nextMonth"), count: due.nextMonth.length, icon: CalendarPlus },
    { key: "redFlags", label: t("coll.tab.redFlags"), count: redFlags.length, icon: Flag },
    { key: "whiteBoard", label: t("coll.tab.whiteBoard"), count: whiteBoard.length, icon: Skull },
  ];

  const isListTab = tab === "redFlags" || tab === "whiteBoard";
  const rawRows = isListTab ? (tab === "redFlags" ? redFlags : whiteBoard) : (due[tab] || []);
  const rows = useSorted(rawRows, sort);

  return (
    <Layout>
      <PageHeader
        title={t("coll.title")}
        subtitle={`${t("coll.subtitle")} · ${due.date}`}
      />

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-5 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3" data-testid="reminders-bar">
        <MessageCircle className="h-4 w-4 text-emerald-600" />
        <span className="text-sm font-medium text-emerald-800">{t("coll.wa_reminders")}</span>
        <span className="text-xs text-emerald-700">{t("coll.wa_desc")}</span>
        <button
          data-testid="send-all-reminders-btn"
          disabled
          title="Bulk auto-send requires the production WhatsApp Business API (not active yet)"
          className="ml-auto bg-slate-200 text-slate-400 rounded-md px-3 py-1.5 text-sm font-medium flex items-center gap-2 cursor-not-allowed"
        >
          <MessageCircle className="h-4 w-4" /> {t("coll.send_all")}
        </button>
      </div>

      <div className="flex items-center gap-1 bg-slate-100 rounded-md p-1 w-fit mb-5" data-testid="collection-tabs">
        {TABS.map((tb) => (
          <button key={tb.key} data-testid={`tab-${tb.key}`} onClick={() => setTab(tb.key)}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center gap-1.5 ${tab === tb.key ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>
            <tb.icon className="h-3.5 w-3.5" /> {tb.label}
            <span className={`text-[11px] rounded-full px-1.5 ${tab === tb.key ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-600"}`}>{tb.count}</span>
          </button>
        ))}
      </div>

      {!isListTab ? (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
          {tab === "nextMonth" && (
            <div className="px-5 pt-4 text-sm text-slate-500">{t("coll.nextmonth_note")}</div>
          )}
          <table className="w-full text-sm" data-testid="due-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <Th label={t("col.code")} sortKey="code" sort={sort} onSort={onSort} className="px-5" />
                <Th label={t("col.customer")} sortKey="customerName" sort={sort} onSort={onSort} />
                <Th label={t("coll.due_day")} sortKey="dueDay" sort={sort} onSort={onSort} align="center" />
                <Th label={t("col.outstanding")} sortKey="outstanding" sort={sort} onSort={onSort} align="right" />
                <Th label={t("col.pending_int")} sortKey="pendingInterest" sort={sort} onSort={onSort} align="right" />
                {tab === "overdue" && <Th label={t("coll.overdue_col")} sortKey="daysOverdue" sort={sort} onSort={onSort} align="right" />}
                <Th label={t("coll.last_pay")} sortKey="daysSinceLastPayment" sort={sort} onSort={onSort} align="right" />
                <Th label={t("col.status")} sort={sort} onSort={onSort} />
                <Th label={t("coll.whatsapp")} sort={sort} onSort={onSort} align="center" />
                <Th label={t("coll.action")} sort={sort} onSort={onSort} align="right" className="px-5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => <DueRow key={r.code} r={r} tab={tab} onWhatsApp={openWhatsApp} showOverdue={tab === "overdue"} />)}
              {rows.length === 0 && <tr><td colSpan={tab === "overdue" ? 10 : 9} className="px-5 py-12 text-center text-slate-400">{t("coll.empty_due")}</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto" data-testid="collection-list">
          {tab === "whiteBoard" && rows.length > 0 && (
            <div className="px-5 pt-4 text-sm text-slate-500">{t("coll.white_note")}</div>
          )}
          <table className="w-full text-sm" data-testid="flags-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <Th label={t("col.code")} sortKey="code" sort={sort} onSort={onSort} className="px-5" />
                <Th label={t("col.customer")} sortKey="customerName" sort={sort} onSort={onSort} />
                <Th label={t("coll.flags")} sort={sort} onSort={onSort} />
                <Th label={t("col.outstanding")} sortKey="outstanding" sort={sort} onSort={onSort} align="right" />
                <Th label={t("col.pending_int")} sortKey="pendingInterest" sort={sort} onSort={onSort} align="right" />
                <Th label={t("coll.last_pay")} sortKey="daysSinceLastPayment" sort={sort} onSort={onSort} align="right" />
                <Th label={t("col.status")} sort={sort} onSort={onSort} />
                <Th label={t("coll.whatsapp")} sort={sort} onSort={onSort} align="center" />
                <Th label={t("coll.action")} sort={sort} onSort={onSort} align="right" className="px-5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isWhite = tab === "whiteBoard";
                return (
                  <tr key={r.code} data-testid={`flag-row-${r.code}`} className="border-b border-slate-50 hover:bg-slate-50">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        {isWhite ? <Skull className="h-4 w-4 text-slate-900 shrink-0" /> : <Flag className="h-4 w-4 text-red-500 shrink-0" />}
                        <Link to={`/loans/${r.code}`} className="font-mono-fig font-medium text-slate-900 hover:underline">{r.code}</Link>
                        {isWhite && <span className="text-[10px] font-bold uppercase tracking-wide bg-slate-900 text-white rounded px-1.5 py-0.5">{t("coll.super_bad")}</span>}
                        {!isWhite && r.inactive && <span className="text-[10px] font-bold uppercase tracking-wide bg-red-100 text-red-700 rounded px-1.5 py-0.5">{t("coll.inactive_badge")}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-3"><Link to={`/customer?q=${encodeURIComponent(r.customerName)}`} className="text-slate-700 hover:text-slate-900 hover:underline">{r.customerName}</Link></td>
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {r.reasons.map((reason) => <span key={reason} className={`text-[11px] font-medium rounded-full px-2 py-0.5 border ${isWhite ? "bg-slate-100 text-slate-700 border-slate-300" : "bg-amber-50 text-amber-800 border-amber-200"}`}>{reason}</span>)}
                      </div>
                    </td>
                    <td className={`px-3 py-3 text-right font-mono-fig font-semibold ${isWhite ? "text-slate-900" : "text-red-600"}`}>{fmtRM(r.outstanding)}</td>
                    <td className="px-3 py-3 text-right font-mono-fig text-red-600">{r.pendingInterest > 0 ? fmtRM(r.pendingInterest) : <span className="text-slate-300">—</span>}</td>
                    <DaysCell days={r.daysSinceLastPayment} />
                    <FollowStatusCell r={r} tab={tab} />
                    <WhatsAppCell r={r} onWhatsApp={openWhatsApp} />
                    <ActionCell r={r} tab={tab} />
                  </tr>
                );
              })}
              {rows.length === 0 && <tr><td colSpan={9} className="px-5 py-12 text-center text-slate-400">{tab === "whiteBoard" ? t("coll.empty_white") : t("coll.empty_red")}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
}
